/* eslint-disable import/no-unresolved */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useTranslation } from 'react-i18next';
import { useLocation, useHistory } from 'react-router-dom';
import { useMutation } from 'react-query';
import { OnboardingBodyLayout, SelectCountryDropdown } from 'assets/components';
import SelectUSStateDropdown from '../../../components/SelectUSStateDropdown';
import { clearFormError, handleFormBlur } from '../../../utilities/formsUtils';
import {
  ADDRESS_STREET_US_MIN_LENGTH,
  validateAddressChange,
} from '../../../utilities/formValidation';
import Input from '../../../components/Input';
import { showAlert } from '../../../utilities/notifications';
import { PARTIAL_SIGN_UP_PHASES, RECAPTCHA_EVENTS } from '../constants';
import { FLOWS, aliasUser, identifyUser, sendStats } from '../../../services/mixpanel';
import {
  cacheRecaptchaV3failure,
  cacheUserFirstAccess,
  clearRecaptchaV3failure,
} from '../../../utilities/caching';
import {
  MXP_ONBOARDING_SIGN_UP_MAILING_ADDRESS_CONFIRM,
  MXP_ONBOARDING_SIGN_UP_MAILING_ADDRESS_VISIT,
} from '../mixpanel-events';
import { PARTIAL_SIGN_UP, USER_SIGNUP } from '../mutations';
import countriesPostcodes from '../../../utilities/countriesPostcodes';
import { OnboardingContext } from '../../../contexts/onboarding/context';
import RecaptchaModal from '../../shared/RecaptchaModal';

const AddMailingAddress = () => {
  const { t: translate } = useTranslation();

  const [form, setForm] = useState({
    address_line_2: '',
    address_line_1: '',
    city: '',
    post_code: '',
    selected_country: '',
    countryName: '',
    state: '',
  });

  const history = useHistory();
  const { state } = useLocation();

  const { executeRecaptcha } = useGoogleReCaptcha();
  const [isOpenRecV2, setIsOpenRecV2] = useState(false);

  const [recV2Token, setRecV2Token] = useState('');

  useEffect(() => {
    if (!state?.email && !state?.partialSignupData?.email) {
      history.push('/onboarding/signup');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { state: onboardingState } = useContext(OnboardingContext);
  const allowPartialOnboarding = onboardingState?.mainSettings?.feature_enable_partial_registration;
  const allowPostcodeValidation =
    onboardingState?.mainSettings?.feature_toggle_enable_postcode_verification_signup;

  useEffect(() => {
    sendStats(MXP_ONBOARDING_SIGN_UP_MAILING_ADDRESS_VISIT, {}, FLOWS.ONBOARDING);
  }, []);

  const selectDropDownFields = [
    {
      type: 'img',
      data: 'iso3166alpha2',
      class: 'col-1 text-left position-relative',
      img: {
        sourcePath: './images/flags/1x1/',
        ext: '.svg',
        class: 'rounded-circle position-absolute top-50 start-0 translate-middle-y',
      },
    },
    {
      type: 'text',
      data: 'displayName',
      class: 'col-12 ps-3 text-left',
    },
  ];

  const stateDropdownFields = [
    {
      type: 'text',
      data: 'postal_abbreviation',
      class: 'col-11 ps-2 text-left',
    },
  ];

  const { mutate: submitUserData, isLoading: isLoadingSubmitForm } = useMutation(USER_SIGNUP, {
    onSettled: (data, error) => {
      sendStats(MXP_ONBOARDING_SIGN_UP_MAILING_ADDRESS_CONFIRM, {}, FLOWS.ONBOARDING);
      aliasUser(data);
      if (!error) {
        setIsOpenRecV2(false);
        clearRecaptchaV3failure();
        cacheUserFirstAccess();
        aliasUser(data);
        identifyUser(data);
        history.push(`/kyc`, {
          ...data,
          signupFlow: true,
        });
      } else if (error?.response?.status === 307) {
        setIsOpenRecV2(true);
        cacheRecaptchaV3failure();
      } else {
        setIsOpenRecV2(false);
        clearRecaptchaV3failure();
        showAlert({ message: error.message, type: 'danger' });
      }
    },
  });

  const { mutate: partialSignup, isLoading: isLoadingPartialSignup } = useMutation(
    PARTIAL_SIGN_UP,
    {
      onSettled: (data, error) => {
        if (error) {
          showAlert({ message: error.message, type: 'danger' });
        } else {
          sendStats(MXP_ONBOARDING_SIGN_UP_MAILING_ADDRESS_CONFIRM, {}, FLOWS.ONBOARDING);
          cacheUserFirstAccess();
          aliasUser(data);
          history.push(`/kyc`, {
            ...data,
            signupFlow: true,
          });
        }
      },
    }
  );

  const validPostcode = useMemo(() => {
    if (allowPostcodeValidation) {
      const countryPostcode = countriesPostcodes.find(
        (country) => country.iso3166alpha2 === state?.selected_country_alpha2
      );
      if (countryPostcode) {
        const regex = new RegExp(countryPostcode.postal);
        const checkPostCode = regex.test(form?.post_code);

        return checkPostCode;
      }
    }
    return true;
  }, [allowPostcodeValidation, form?.post_code, state?.selected_country_alpha2]);

  const isDisabled = useMemo(
    () =>
      form.address_line_1 === '' || form.city === '' || form.post_code === '' || form.state === '',
    [form]
  );

  const handleChange = (e, fieldName) => {
    if (!e) return;
    if (!e.target) {
      if (fieldName === 'post_code') {
        setForm({ ...form, [fieldName]: e?.toUpperCase() });
      } else {
        setForm({ ...form, [fieldName]: e });
      }
    } else {
      const { name, value } = e.target;
      if (name === 'post_code') {
        setForm({ ...form, [name]: value?.toUpperCase() });
      } else if (value) {
        const validAddress = validateAddressChange(
          value,
          true,
          name === 'address_line_1' ? 'street' : 'houseNumber'
        );
        if (validAddress) setForm({ ...form, [name]: value });
      } else {
        setForm({ ...form, [name]: '' });
      }
    }
  };

  const handleSubmitForm = async (e) => {
    e.preventDefault();

    const validStreetAddress = validateAddressChange(
      form?.address_line_1,
      true, // mailing address available only for US-customers
      'street',
      false
    );
    if (!validStreetAddress) {
      if (form?.address_line_1?.length < ADDRESS_STREET_US_MIN_LENGTH) {
        showAlert({
          message: translate('error_address1_is_too_short_message_us'),
          type: 'danger',
        });
      } else {
        showAlert({
          message: translate('error_address1_contains_post_office_words_message_us'),
          type: 'danger',
        });
      }

      return;
    }

    if (!validPostcode) {
      showAlert({
        message: translate('postcode_error_message'),
        type: 'danger',
      });
      return;
    }

    try {
      const token = await executeRecaptcha(RECAPTCHA_EVENTS.SIGNUP_VERIFY_EMAIL);
      if (!token) {
        // eslint-disable-next-line consistent-return
        return false;
      }

      if (allowPartialOnboarding) {
        partialSignup({
          id: state?.partialSignupData?.id,
          phase: PARTIAL_SIGN_UP_PHASES.MAILING_ADDRESS,
          city: form.city,
          state: form.state,
          address_line_1: form.address_line_1,
          address_line_2: form.address_line_2,
          post_code: form.post_code,
          verification_id: state?.verification_id,
        });
      } else {
        submitUserData({
          ...state,
          mailingAddress: {
            city: form.city,
            state: form.state,
            address_line_1: form.address_line_1,
            address_line_2: form.address_line_2,
            post_code: form.post_code,
          },
          token,
        });
      }
    } catch (error) {
      // eslint-disable-next-line consistent-return
      return false;
    }
  };

  const renderButtons = () => (
    <>
      <div className="d-none d-md-block w-100">
        <button
          className="btn btn-primary w-100 text-bold"
          type="submit"
          loading={isLoadingSubmitForm || isLoadingPartialSignup ? 'true' : 'false'}
          form="mailing-address-form"
          disabled={isDisabled}>
          {translate('confirm_button_label')}
        </button>
      </div>
      <div className="d-block d-md-none position-fixed bottom-0 bg-lighter w-100 py-3 px-3">
        <button
          className="btn btn-primary w-100 text-bold"
          type="submit"
          loading={isLoadingSubmitForm || isLoadingPartialSignup ? 'true' : 'false'}
          form="mailing-address-form"
          disabled={isDisabled}>
          {translate('confirm_button_label')}
        </button>
      </div>
    </>
  );

  const onChangeRecV2 = async (tokenV2) => {
    setRecV2Token(tokenV2);
    submitUserData({
      ...state,
      mailingAddress: {
        city: form.city,
        state: form.state,
        address_line_1: form.address_line_1,
        address_line_2: form.address_line_2,
        post_code: form.post_code,
      },
      token: tokenV2,
    });
  };

  return (
    <>
      <OnboardingBodyLayout
        withBack={!state?.hideBackBtn}
        onPrevClick={history.goBack}
        renderButtons={renderButtons}
        title={translate('mailing_address_label')}>
        <form onSubmit={handleSubmitForm} id="mailing-address-form">
          <div className="d-flex flex-row w-100 flex-fill gap-4">
            <div className="select-group personal-info form-group w-100 mb-0 mt-4">
              <SelectCountryDropdown
                name="selected_country"
                placeholder={translate('country_select_label')}
                getOptionLabel={(option) => option.displayName}
                isOptionSelected={(option) =>
                  option.iso3166alpha3 === state?.countryOfResidenceAlpha ||
                  option.iso3166alpha3 === state?.selected_country_alpha
                }
                value={state?.selected_country_label || state?.countryOfResidenceLabel}
                flagValue={state?.selected_country_alpha2 || state?.countryOfResidenceAlpha2}
                onChange={(data, name) => handleChange(data, name)}
                handleBlur={handleFormBlur}
                clearError={clearFormError}
                optionName="displayName"
                prefix=""
                fieldsInfo={selectDropDownFields}
                dropDownParentClass=""
                withValueImageFlag
                readOnly
              />
            </div>

            <div className="select-group personal-info form-group w-100 mb-0 mt-4">
              <SelectUSStateDropdown
                name="state"
                placeholder={translate('us_state_select_label')}
                getOptionLabel={(option) => option.postal_abbreviation}
                isOptionSelected={(option) => option.postal_abbreviation === form.state}
                value={form.state}
                onChange={(data, name) => handleChange(data.postal_abbreviation, name)}
                handleBlur={handleFormBlur}
                clearError={clearFormError}
                optionName="postal_abbreviation"
                prefix=""
                fieldsInfo={stateDropdownFields}
                dropDownParentClass=""
                withValueImageFlag
              />
            </div>
          </div>
          <div className="d-flex flex-row w-100 flex-fill gap-4">
            <Input
              containerClass="w-100"
              groupClassName="mb-0"
              placeholder={translate('city_input_label')}
              label={translate('city_input_label')}
              errorLabel={translate('input_required_error_label')}
              name="city"
              value={form.city}
              onChange={handleChange}
              onBlur={handleFormBlur}
              onFocus={clearFormError}
              withFloatingLabel
              withErrorLabel
              required
            />
            <Input
              containerClass="w-100"
              groupClassName="mb-0"
              placeholder={translate('index_input_label')}
              label={translate('index_input_label')}
              errorLabel={translate('input_required_error_label')}
              name="post_code"
              value={form.post_code}
              onChange={handleChange}
              onBlur={handleFormBlur}
              onFocus={clearFormError}
              withFloatingLabel
              withErrorLabel
              required
            />
          </div>
          <Input
            containerClass="w-100"
            groupClassName="mb-0"
            placeholder={translate('street_address_input_label')}
            label={translate('address_line_1_label_street_address')}
            errorLabel={translate('input_required_error_label')}
            name="address_line_1"
            value={form.address_line_1}
            onChange={handleChange}
            onBlur={handleFormBlur}
            onFocus={clearFormError}
            withFloatingLabel
            withErrorLabel
            required
          />
          <Input
            containerClass="w-100"
            groupClassName="mb-0"
            placeholder={translate('house_apartment_input_label')}
            label={translate('address_line_2_label_house_number')}
            errorLabel={translate('input_required_error_label')}
            name="address_line_2"
            value={form.address_line_2}
            onChange={handleChange}
            onBlur={handleFormBlur}
            onFocus={clearFormError}
            withFloatingLabel
            withErrorLabel
          />
        </form>
      </OnboardingBodyLayout>
      <RecaptchaModal
        onChange={onChangeRecV2}
        isLoading={isLoadingSubmitForm}
        resetToken={() => setRecV2Token('')}
        token={recV2Token}
        setIsModalVisible={setIsOpenRecV2}
        isModalVisible={isOpenRecV2}
      />
    </>
  );
};

export default AddMailingAddress;
